'use client'
import {useTekstClient} from "hooks/useTekstClient";
import {Alert} from "components/ui";
import React, {useEffect, useState} from "react";
import {startsWith} from "lodash";
import {EyeSlashFilledIcon} from "./EyeSlashFilledIcon";
import {EyeFilledIcon} from "./EyeFilledIcon";
import {useSearchParams} from 'next/navigation'
import {signIn} from "next-auth/react"
import useSWR from "swr";
import Link from "next/link";
import LocalizedLinkClient from 'components/LocalizedLink/LocalizedLinkClient';


export const FormInloggen = () => {
    const {tekst} = useTekstClient();
    const [isVisible, setIsVisible] = useState(false);
    const [pending, setPending] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const searchParams = useSearchParams()
    const callbackUrl = searchParams.get('callbackUrl') || '/mijn-mkg/dashboard'
    const [errorMessage, setErrorMessage] = useState(searchParams.get('error'))
    const [hasMounted, setHasMounted] = useState(false);


    const toggleVisibility = () => setIsVisible(!isVisible);

    const finishLoginPopupSuccess = () => {
        try {
            if (window.opener && !window.opener.closed) {
                window.opener.location.reload();
            }
        } catch (_e) {
            /* opener may be restricted */
        }
        window.close();
    };

    const isLoginPopup = searchParams.get("popup") === "1";

    const handleClick = (event) => {
        event.preventDefault();
        setPending(true);
        setErrorMessage(null);

        if (isLoginPopup) {
            signIn("credentials", {
                email,
                password,
                redirect: false,
            })
                .then((response) => {
                    if (response?.error) {
                        setErrorMessage(response.error);
                        return;
                    }
                    if (response?.ok) {
                        finishLoginPopupSuccess();
                    }
                })
                .finally(() => {
                    setPending(false);
                });
            return;
        }

        signIn("credentials", {email: email, password: password})
            .then((response) => {
                setErrorMessage(response.error);
            })
            .finally(() => {
                setPending(false);
            });
    };

    useEffect(() => {
        setHasMounted(true);
    }, []);


    // Only while the free intro period runs; hidden while loading or if the check fails
    const {data: ferroIntro} = useSWR(`${process.env.NEXT_PUBLIC_BACKEND}/api/public/support/ferro-intro`,
        (url) => fetch(url).then((res) => res.json()),
        {revalidateOnFocus: false, shouldRetryOnError: false});
    const ferroNotice = ferroIntro?.active ? tekst('inloggen:ferro gratis proberen', null, null, true) : '';

    return <div className="flex min-h-full flex-col justify-center px-6 pb-12 lg:px-8">
        {/* The translation marks the clickable part as [[...]] so each language can place it */}
        {ferroNotice.trim() && <div className="sm:mx-auto sm:w-full sm:max-w-sm mb-6 flex items-center gap-2 border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
            <img alt="Ferro" src="/images/ferro-avatar.png" style={{'width': '32px'}} className="shrink-0"/>
            <span>
                {ferroNotice.split(/\[\[(.+?)\]\]/).map((deel, i) => i % 2 === 1
                    ? <LocalizedLinkClient key={i} href="/mijn-mkg/support/kenniscentrum/id/14158" prefetch={false} className="font-semibold text-darkBlue hover:text-lightCyan">{deel}</LocalizedLinkClient>
                    : deel)}
            </span>
        </div>}
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">{tekst('algemeen:inloggen')}</h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm space-y-6">
            <form onSubmit={handleClick}>
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        {tekst('algemeen:email adres')}
                    </label>
                    <div className="mt-2">
                        <input
                            id="email"
                            name="email"
                            type="email"
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                            className="block w-full rounded-none border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between pt-3">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            {tekst('algemeen:wachtwoord')}
                        </label>
                        <div className="text-sm">
                            <LocalizedLinkClient href="/mijn-mkg/wachtwoord/herstel" prefetch={false} className="font-semibold text-blue-600 hover:text-blue-500">
                                {tekst('algemeen:wachtwoord vergeten?')}
                            </LocalizedLinkClient>
                        </div>
                    </div>
                    <div className="mt-2 relative">
                        <input
                            id="password"
                            name="password"
                            type={isVisible ? "text" : "password"}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                            className="block w-full rounded-none border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={toggleVisibility}
                            aria-label="toggle password visibility"
                            className="absolute inset-y-0 right-2 flex items-center bg-transparent border-0 focus:outline-none"
                        >
                            {isVisible ? (
                                <EyeSlashFilledIcon className="h-5 w-5 ml-8 text-gray-400" />
                            ) : (
                                <EyeFilledIcon className="h-5 w-5 ml-8 text-gray-400" />
                            )}
                        </button>
                    </div>


                </div>


                <div>
                    <button type={"submit"} disabled={pending} aria-disabled={pending}
                            className={'block w-full btn btn-primary mt-3 py-2 px-3'}>{tekst('algemeen:inloggen')}</button>
                </div>
            </form>
            <hr/>

            <Link
                href={`${process.env.NEXT_PUBLIC_BACKEND}/saml2/mkg/login?returnTo=${encodeURIComponent(callbackUrl)}`}>{tekst('algemeen:Inloggen als behandelaar')}</Link>


            {errorMessage && <Alert variant="warning">
                <p>
                    {startsWith(errorMessage, 'Invalid credentials')
                        ? tekst('pagina:inloggen:inloggegevens zijn incorrect')
                        : errorMessage
                    }
                </p>
            </Alert>}


        </div>
    </div>
}